import { type UseMutationResult } from '@tanstack/react-query';

import { useApiMutation } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { parseOrThrow } from '../../../lib/api/contract.js';
import { roleEndpoints } from './endpoints.js';
import {
  createdRole,
  roleDetail,
  type CreatedRole,
  type RoleDetail,
  type RoleFormInput,
} from './schemas.js';

/**
 * Writes for the Roles module.
 *
 * The shape every mutation in the portal copies:
 *
 * - **An idempotency key per submission.** The Gateway retains keys for 24
 *   hours, so a double-submitted form is one role rather than two. Fresh per
 *   call — a stable key would make the second role a replay of the first.
 * - **Invalidate the lists and the details**, not a hand-listed set of pages.
 * - **The response is parsed.** A create returning an unexpected shape is the
 *   same contract failure as a read that returns one.
 * - **Errors are not swallowed** — `ApiError.fieldErrors` reaches the caller so
 *   the form can attach each message to the field that caused it.
 */
const WRITTEN_KEYS = [queryKeys.roles.lists, queryKeys.roles.details];

/**
 * The fields the form collects that the service treats as optional.
 *
 * A create **omits** an empty one so the service applies its own default, while
 * an edit sends `null` to clear a previous value. Sending `''` would write an
 * empty string into a column whose emptiness is spelled `null` elsewhere.
 */
export function createRolePayload(values: RoleFormInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: values.name,
    permissions: values.permissions,
  };
  if (values.description !== undefined && values.description !== '') {
    payload.description = values.description;
  }
  return payload;
}

export function updateRolePayload(values: RoleFormInput): Record<string, unknown> {
  return {
    name: values.name,
    permissions: values.permissions,
    description: values.description === '' ? null : values.description,
  };
}

export function useCreateRole(): UseMutationResult<CreatedRole, unknown, RoleFormInput> {
  return useApiMutation<RoleFormInput, CreatedRole>({
    mutationFn: async (api, input) => {
      const payload = await api.post<unknown>(
        roleEndpoints.create,
        createRolePayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(createdRole, payload, roleEndpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateRoleVariables {
  readonly roleId: string;
  readonly values: RoleFormInput;
}

export function useUpdateRole(): UseMutationResult<RoleDetail, unknown, UpdateRoleVariables> {
  return useApiMutation<UpdateRoleVariables, RoleDetail>({
    mutationFn: async (api, { roleId, values }) => {
      const payload = await api.patch<unknown>(
        roleEndpoints.update(roleId),
        updateRolePayload(values),
      );
      return parseOrThrow(roleDetail, payload, roleEndpoints.update(roleId));
    },
    invalidate: WRITTEN_KEYS,
  });
}

/**
 * Deleting a role.
 *
 * Soft, always: the row stays in the database and the users who held the role
 * lose it. A role that vanishes cannot still be in force.
 */
export function useSoftDeleteRole(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, roleId) => {
      await api.delete<unknown>(roleEndpoints.delete(roleId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
